import { getIsOnline } from "@client/hooks/useNetworkStatus";
import { shouldRetryUserQuery } from "@client/offline/queryRetryPolicy";
import { trpc } from "@client/trpc";
import type { FeatureFlagKey } from "@lib/featureFlags";
import { useEffectiveUser } from "./use-effective-user";

/**
 * Native feature flag check, replacing the PostHog-backed hook of the same
 * name for in-app flags (the boot-time `native-sentry-reporting` gate stays
 * on the PostHog snapshot — it runs before auth exists). Flags are resolved
 * for the viewer on the server (audience set on the Feature Flags admin
 * page); one query serves every flag and every call site.
 *
 * `undefined` while the query hasn't resolved, matching the PostHog hook's
 * contract — callers treat it as "not enabled" (fail closed) or as "unknown"
 * for tri-state UI.
 */
export function useFeatureFlagEnabled(
  flag: FeatureFlagKey,
): boolean | undefined {
  return useFeatureFlags().data?.[flag];
}

export function useFeatureFlags() {
  const { data: viewer } = useEffectiveUser();
  const query = trpc.getEnabledFeatureFlags.useQuery(undefined, {
    // The shared header also mounts on the public sign-in page.
    enabled: viewer != null,
    staleTime: 60_000,
    retry: (failureCount, error) =>
      shouldRetryUserQuery({
        failureCount,
        isUnauthorized: error.data?.code === "UNAUTHORIZED",
        isOnline: getIsOnline(),
      }),
    // The app disables refetchOnWindowFocus globally, so without polling an
    // open session would never see a flip — a kill switch has to land in
    // sessions that stay open. PostHog's SDK polled for us; this replaces it.
    refetchInterval: 60_000,
  });
  // Invalidation retains data during impersonation switches, including when
  // refetching fails. Never apply another viewer's feature audience.
  return {
    ...query,
    data:
      viewer && query.data?.viewerId === viewer.userId ? query.data : undefined,
  };
}
